/**
 * Tab-side feed-filter needs registry (consumer feed filters, Stage C).
 *
 * Views (and the account plane) declare what they need from the markets
 * feed beyond the game-centric defaults — the normally-excluded classes /
 * market types they render, and (Phase 2 only) the sports they render. The
 * registry unions across every source in this tab and hands the result to
 * the fleet (useHydra's sink), which forwards each feed's share to its
 * transport: the SharedWorker unions again across tabs and applies the
 * narrowing grace; the direct-WS client does the same locally. Exactly the
 * `setOrderbookNeeds` → `_routeOrderbookNeeds` shape, one layer over.
 *
 * Outside Zustand on purpose (like `_viewOrderbookNeeds`): a need change must
 * never re-render anything — it only changes what the wire carries.
 *
 * No grace at THIS layer: the transport layers own the 30 s narrowing grace
 * (worker per-port aggregator / direct-client aggregator), and adding a
 * second one here would double the time a dropped opt-in keeps flowing. A
 * short coalesce window is kept so a route change that mounts several
 * declaring components produces ONE publish.
 */
import {
  FeedFilterAggregator,
  type AggregatedNeeds,
  type FeedNeeds,
} from '../workers/feedFilter';

export type FeedNeedsSink = (needs: AggregatedNeeds) => void;

let _sink: FeedNeedsSink | null = null;
let _last: AggregatedNeeds | null = null;

const _aggregator = new FeedFilterAggregator<string>(
  (needs) => {
    _last = needs;
    _sink?.(needs);
  },
  { narrowingGraceMs: 0, emitDebounceMs: 0 },
);

/** Declare (or clear, with `null`) one source's needs. Idempotent per key. */
export function declareFeedNeeds(sourceId: string, needs: FeedNeeds | null): void {
  _aggregator.setNeeds(sourceId, needs);
}

/** Install the fleet publisher. Called by useHydra when the fleet mounts; the
 *  current union is published immediately so needs declared before the
 *  sockets existed are not lost. `null` detaches (fleet teardown). */
export function setFeedNeedsSink(sink: FeedNeedsSink | null): void {
  _sink = sink;
  if (sink) sink(_last ?? _aggregator.current());
}

/** The current union (diagnostics + tests). */
export function currentFeedNeeds(): AggregatedNeeds {
  return _aggregator.current();
}

/** Convert the union back into a per-transport declaration. */
export function toFeedNeeds(needs: AggregatedNeeds): FeedNeeds {
  return {
    classes: [...needs.classes],
    marketTypes: [...needs.marketTypes],
    sports: needs.sports == null ? 'all' : [...needs.sports],
    // Danielle R17 DNL-015: this is the PUBLISHER boundary — every declared
    // axis must cross it or the wire silently keeps the wider scope. Dropping
    // `games` here meant the declarations were correct, the filter builder was
    // correct, and production still went out sport-only. Any axis added to
    // AggregatedNeeds must be added here in the same commit.
    games: needs.games == null ? 'all' : [...needs.games],
  };
}
